import React, { useLayoutEffect, useRef, useState } from "react";
import useBreakpoint from "../../../../utils/useBreakpoint";
import { LandTourDetail } from "../../../../interfaces/land-tour.interface";
import GroundTourModal from "./GroundTourModal";

const ICONS = "/iceholidays-assets/images/icons";

interface Props {
  landTour: LandTourDetail;
}

// Every tag on the tour, primary first and highlighted (#811). Collapsed to one
// row: desktop expands in place via "Show All", mobile opens a bottom sheet.
export default function GroundTourPromotions({ landTour }: Props) {
  const isMobile = useBreakpoint();
  const listRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const { primaryTag, otherTags } = landTour;
  const tagsKey = [primaryTag?.id, ...otherTags.map((tag) => tag.id)].join("|");

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const measure = () => setOverflowing(el.scrollHeight > el.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [tagsKey, expanded]);

  if (!primaryTag && otherTags.length === 0) return null;

  const chips = (
    <>
      {primaryTag && (
        <span className="ground-tour-show-v2__promo-tag ground-tour-show-v2__promo-tag--primary">{primaryTag.name}</span>
      )}
      {otherTags.map((tag) => (
        <span className="ground-tour-show-v2__promo-tag" key={tag.id}>{tag.name}</span>
      ))}
    </>
  );

  if (isMobile) {
    return (
      <section className="ground-tour-show-v2__promotions">
        <h2 className="ground-tour-show-v2__section-title">Promotions</h2>

        <div className="ground-tour-show-v2__promo-row">
          <div ref={listRef} className="ground-tour-show-v2__promo-list">{chips}</div>

          {overflowing && (
            <button
              type="button"
              className="ground-tour-show-v2__promo-more"
              aria-label="View all promotions"
              onClick={() => setSheetOpen(true)}
            >
              <img src={`${ICONS}/icon-promotions-chevron.svg`} alt="" aria-hidden="true" />
            </button>
          )}
        </div>

        <GroundTourModal open={sheetOpen} onClose={() => setSheetOpen(false)} title="Promotions" isMobile>
          <div className="ground-tour-show-v2__promo-list is-expanded">{chips}</div>
        </GroundTourModal>
      </section>
    );
  }

  return (
    <section className="ground-tour-show-v2__promotions">
      <h2 className="ground-tour-show-v2__section-title">Promotions</h2>

      <div ref={listRef} className={`ground-tour-show-v2__promo-list ${expanded ? "is-expanded" : ""}`}>
        {chips}
      </div>

      {overflowing && !expanded && (
        <button type="button" className="ground-tour-show-v2__promo-show-all" onClick={() => setExpanded(true)}>
          Show All
        </button>
      )}
    </section>
  );
}
